import { useState, useEffect } from "react";
import Icon from "../../components/ui/Icon";
import SlideOverlay from "../../components/overlays/SlideOverlay";
import PageHeader from "../../components/shared/PageHeader";
import SignalBars from "../../components/ui/SignalBars";
import { rssiStrength } from "../../constants/gateways";
import { useAppState } from "../../state/appState";
import { InfoTile } from "./networkUtils";

export default function GatewayDetailsOverlay({ isOpen, gateway, nodeCount, isScanned, onClose }) {
  const { actions } = useAppState();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    if (!isOpen) setIsEditing(false);
  }, [isOpen]);

  const n = gateway ?? {};
  const rssi = n.rssi ?? -70;
  const strength = rssiStrength(rssi);
  const qualityLabel = strength === 3 ? "Excellent" : strength === 2 ? "Good" : "Fair";

  const handleSave = () => {
    const trimmed = editName.trim() || n.name;
    if (isScanned) {
      actions.updateNodeMeta(n.id, { name: trimmed });
    } else {
      actions.updateGatewayMeta({ name: trimmed });
    }
    setIsEditing(false);
  };

  return (
    <SlideOverlay isOpen={isOpen}>
      <div className="h-full overflow-y-auto bg-brand-navy px-4 pb-6">
        <div className="mx-auto w-full max-w-xl space-y-4 pt-4">

          <PageHeader title={`Gateway: ${n.name ?? "cBerry Gateway"}`} onBack={onClose} />

          {/* ── Identity ─────────────────────────────────────────────────── */}
          <div className="rounded-3xl border border-emerald-500/30 bg-brand-purple px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10">
                <Icon name="bluetooth_connected" className="text-xl text-emerald-400" />
              </span>
              <div>
                <p className="text-sm font-black italic text-brand-light">{n.name ?? "cBerry Gateway"}</p>
                <p className="text-[11px] text-brand-light/50">Bluetooth-CAN Bridge</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="text-xs font-semibold text-emerald-400">Connected</span>
            </div>
          </div>

          {/* ── Node Details ─────────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
                Node Details
              </h2>
              {isEditing ? (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="rounded-xl bg-brand-magenta px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-pink transition"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="rounded-xl border border-brand-navy bg-brand-navy px-2.5 py-1 text-[11px] font-semibold text-brand-light/70 hover:border-brand-magenta/60 transition"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setEditName(n.name ?? ""); setIsEditing(true); }}
                  className="flex items-center gap-1 rounded-xl border border-brand-navy bg-brand-navy px-2.5 py-1 text-[11px] font-semibold text-brand-light/70 hover:border-brand-magenta/60 hover:text-brand-magenta transition"
                >
                  <Icon name="edit" className="text-sm" />
                  Edit
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="rounded-lg border border-brand-magenta/60 bg-brand-navy px-2 py-1 text-sm font-semibold text-brand-light outline-none focus:border-brand-magenta"
                  />
                ) : (
                  <span className="text-sm font-semibold text-brand-light">{n.name ?? "—"}</span>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Type</span>
                <span className="text-sm font-semibold text-brand-light">BT-CAN Bridge</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">BT Address</span>
                <span className="text-xs font-semibold text-brand-light font-mono">{n.btAddress ?? "—"}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">Status</span>
                <div className="flex items-center gap-1.5">
                  <Icon name="check_circle" className="text-base text-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-400">Online</span>
                </div>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">HW Version</span>
                <span className="text-sm font-semibold text-brand-light">{n.hw ?? "—"}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold tracking-widest text-brand-light/40 uppercase">FW Version</span>
                <span className="text-sm font-semibold text-brand-light">{n.fw ?? "—"}</span>
              </div>
            </div>
          </div>

          {/* ── Bluetooth Signal ─────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
              Bluetooth Signal
            </h2>
            <div className="flex items-center gap-5">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-brand-navy bg-brand-navy">
                <SignalBars strength={strength} />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-2xl font-black italic text-brand-light">{rssi} dBm</span>
                <span className="text-xs text-brand-light/50">{qualityLabel} signal</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InfoTile label="Protocol" value="BLE 5.0" />
              <InfoTile label="Quality" value={qualityLabel} />
            </div>
          </div>

          {/* ── CAN Bus Bridge ───────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
              CAN Bus Bridge
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <InfoTile label="Bus Speed" value="250 kbps" />
              <InfoTile label="Nodes Active" value={`${nodeCount}`} />
              <InfoTile label="Protocol" value="CANopen" />
              <InfoTile label="Mode" value="Bridge" />
            </div>
          </div>

          {/* ── Firmware ─────────────────────────────────────────────────── */}
          <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-4 space-y-3">
            <h2 className="text-xs font-black italic tracking-widest text-brand-light/50 uppercase">
              Firmware
            </h2>
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 flex items-center gap-2">
              <Icon name="check_circle" className="text-base text-emerald-400" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-emerald-400">Up to date</span>
                <span className="text-[10px] text-brand-light/40">{n.fw ?? "—"} is the latest version</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </SlideOverlay>
  );
}
